'use client';

import React, { useContext, useEffect, useState } from 'react';
import { CardContent, CardHeader, CardTitle } from '../card';
import { Step, StepType } from '../../chat/types/base_step';
import { OriginHost, ProductLine } from '../../helpers/home_services.helper';
import {
  getInitialStep,
  getStepAmounts,
  getStepByIndex,
} from '../../chat/chat';
import Question from '../questions/question';
import SuccessCard from './success_card';
import InitialCardContent from './initial_card_content';
import StoreProvider from '../../context/provider';
import { StoreContext } from '../../context';
import { ChatAnswersDTO } from '../../chat/answers/chat_answers';
import {
  getObjectSessionStorageItem,
  SessionStorageKey,
  UserSessionData,
} from '../../storage/session_storage';
import { auditEvent, postLead } from '@/src/services/api';

interface HeroCardClientContentProps {
  productLine: ProductLine;
  cardTitle: string;
}

export interface Answer {
  attributeName: string;
  value: any;
}

export default function HeroCardClientContent({
  productLine,
  cardTitle,
}: HeroCardClientContentProps) {
  const context = useContext(StoreContext);
  const stepAmount = getStepAmounts(productLine);
  const [currentStep, setCurrentStep] = useState<Step>(
    getInitialStep(productLine)
  );
  const [stepsIndex, setStepIndex] = useState<number>(0);
  const [stepPercentage, setStepPercentage] = useState<number>(0);
  const [answers, setAnswers] = useState<Partial<ChatAnswersDTO>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isStartSubmitted, setIsStartSubmitted] = useState(false);
  const originHost = OriginHost.Top10HomeServices;

  const buildLeadData = (updatedAnswers?: Partial<ChatAnswersDTO>) => {
    const { userPublicId, campaignId, sourceId, sourceType, sourceName } =
      context;
    const answersData = updatedAnswers ?? answers;
    const leadData = {
      userPublicId,
      originHost,
      answers: {
        ...answersData,
        landingProductLine: productLine,
      },
      sourceId,
      sourceType,
      sourceName,
      campaignId,
    };
    return leadData;
  };

  const goToNextStep = (updatedAnswers?: Partial<ChatAnswersDTO>) => {
    if (!currentStep) return;
    const newStepIndex = stepsIndex + 1;
    const next = getStepByIndex(productLine, newStepIndex);
    if (next == null) {
      setIsStartSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(true);
        setIsStartSubmitted(false);
        postLead(buildLeadData(updatedAnswers));
      }, 1500);
    } else {
      setCurrentStep(next);
      setStepIndex(newStepIndex);
    }
  };

  const sendAuditEvent = (answers: Answer[]) => {
    const auditEvents = answers
      .filter(
        answer =>
          !answer.attributeName.match(/\bleadJornayaToken\b/i) &&
          !answer.attributeName.match(/\btrustedFormCertUrl\b/i)
      )
      .map(answer => ({
        key: answer.attributeName,
        value: `${answer.value}`,
      }));

    const auditEventData = {
      auditEvents: auditEvents,
      userPublicId: context.userPublicId,
      sourceType: context.sourceType,
      originHost,
    };

    auditEvent(auditEventData, productLine);
  };

  const handleBack = () => {
    if (stepsIndex > 0) {
      const newStepIndex = stepsIndex - 1;
      const prevStep = getStepByIndex(productLine, newStepIndex);
      setCurrentStep(prevStep!);
      setStepIndex(newStepIndex);
    }
  };

  const handleReset = () => {
    const firstStep = getInitialStep(productLine);
    setCurrentStep(firstStep);
    setStepIndex(0);
    setIsSubmitted(false);
  };

  const handleAnswersSubmitted = (answersValues: Answer[]) => {
    const updatedAnswers = answersValues.reduce(
      (acc, { attributeName, value }) => {
        acc[attributeName as keyof ChatAnswersDTO] = value;
        return acc;
      },
      { ...answers }
    );

    setAnswers(updatedAnswers);
    goToNextStep(updatedAnswers);
    sendAuditEvent(answersValues);
  };

  useEffect(() => {
    const userSessionData = getObjectSessionStorageItem<UserSessionData>(
      SessionStorageKey.userData
    );
    if (userSessionData?.answerAttrs != null) {
      setAnswers(userSessionData.answerAttrs[productLine]);
    }
  }, [productLine]);

  useEffect(
    () => {
      context.setAnswerAttrsCallback(productLine, answers);
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [answers]
  );

  useEffect(() => {
    if (stepAmount > 1) {
      const percentage = Math.floor((stepsIndex / (stepAmount - 1)) * 99);
      setStepPercentage(percentage);
    }
  }, [stepsIndex, stepAmount]);

  if (isSubmitted === true) {
    return <SuccessCard onReset={handleReset} />;
  }

  return (
    <>
      <CardHeader className="p-1 rounded-t-xl bg-emerald-600">
        <div className="relative text-center">
          <CardTitle className="text-2xl font-bold text-white px-6 py-3">
            {cardTitle}
          </CardTitle>
        </div>
      </CardHeader>

      {stepsIndex > 0 && (
        <CardContent className="flex-grow pt-4 pb-2 px-6">
          <Question
            currentStep={currentStep}
            stepPercentage={stepPercentage}
            onClickBack={handleBack}
            onSubmit={handleAnswersSubmitted}
            answers={answers}
            isStartSubmitted={isStartSubmitted}
          />
        </CardContent>
      )}

      {currentStep?.type === StepType.ZipCode && (
        <InitialCardContent
          goToNextStep={goToNextStep}
          onSubmit={handleAnswersSubmitted}
        />
      )}
    </>
  );
}
